import { Link } from "react-router-dom";
import MangaCard from "@/components/feature/MangaCard";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { mangaList, categories, siteInfo } from "@/mocks/manga";

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="flex items-center gap-1 text-background-50 text-sm">
      <i className="ri-star-fill text-accent-500"></i>
      <span className="font-semibold">{rating.toFixed(1)}</span>
    </span>
  );
}

const categoryCards = [
  { name: "热血", image: "https://readdy.ai/api/search-image?query=action%20manga%20comic%20illustration%20scene%20of%20fierce%20fighter%20with%20energy%20aura%20in%20dynamic%20battle%20pose%2C%20warm%20crimson%20and%20orange%20palette%2C%20vibrant%20anime%20artwork&width=600&height=400&seq=cat-1-manga&orientation=landscape" },
  { name: "恋爱", image: "https://readdy.ai/api/search-image?query=romance%20manga%20comic%20illustration%20scene%20of%20couple%20under%20cherry%20blossoms%2C%20soft%20warm%20pink%20and%20golden%20light%2C%20sweet%20anime%20artwork&width=600&height=400&seq=cat-2-manga&orientation=landscape" },
  { name: "悬疑", image: "https://readdy.ai/api/search-image?query=mystery%20manga%20comic%20illustration%20scene%20of%20detective%20in%20dark%20alley%20with%20spotlight%2C%20moody%20amber%20and%20dark%20tones%2C%20suspenseful%20anime%20artwork&width=600&height=400&seq=cat-3-manga&orientation=landscape" },
  { name: "科幻", image: "https://readdy.ai/api/search-image?query=science%20fiction%20manga%20comic%20illustration%20scene%20of%20futuristic%20mecha%20and%20cyberpunk%20city%2C%20warm%20orange%20and%20teal%20neon%2C%20high%20detail%20anime%20artwork&width=600&height=400&seq=cat-4-manga&orientation=landscape" },
  { name: "搞笑", image: "https://readdy.ai/api/search-image?query=comedy%20manga%20comic%20illustration%20scene%20of%20funny%20chibi%20characters%20laughing%20with%20exaggerated%20expressions%2C%20bright%20warm%20yellow%20palette%2C%20playful%20anime%20artwork&width=600&height=400&seq=cat-5-manga&orientation=landscape" },
  { name: "冒险", image: "https://readdy.ai/api/search-image?query=adventure%20manga%20comic%20illustration%20scene%20of%20explorers%20in%20a%20mysterious%20jungle%20with%20sunlight%20rays%2C%20warm%20golden%20and%20green%20tones%2C%20epic%20anime%20artwork&width=600&height=400&seq=cat-6-manga&orientation=landscape" },
  { name: "奇幻", image: "https://readdy.ai/api/search-image?query=fantasy%20manga%20comic%20illustration%20scene%20of%20mage%20casting%20magic%20in%20a%20glowing%20enchanted%20forest%2C%20luminous%20warm%20gold%20and%20magical%20light%2C%20whimsical%20anime%20artwork&width=600&height=400&seq=cat-7-manga&orientation=landscape" },
  { name: "校园", image: "https://readdy.ai/api/search-image?query=school%20life%20manga%20comic%20illustration%20scene%20of%20students%20on%20a%20sunlit%20campus%20during%20sports%20festival%2C%20nostalgic%20warm%20afternoon%20light%2C%20sweet%20anime%20artwork&width=600&height=400&seq=cat-8-manga&orientation=landscape" },
];

export default function Home() {
  const featured = mangaList.slice(0, 4);
  const latest = [...mangaList].sort((a, b) => b.year - a.year).slice(0, 8);
  const ranking = [...mangaList].sort((a, b) => b.likes - a.likes).slice(0, 8);
  const hot = mangaList.slice(0, 8);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: siteInfo.name,
            url: "https://mangains.github.io/",
            logo: "https://readdy.ai/api/search-image?query=manga%20book%20open%20logo%20icon%20in%20warm%20orange%2C%20minimal%20flat%20design&width=600&height=600&seq=org-logo-01&orientation=squarish",
            slogan: siteInfo.slogan,
            description: siteInfo.description,
            email: siteInfo.email,
            telephone: siteInfo.phone,
            address: { "@type": "PostalAddress", streetAddress: siteInfo.address, addressCountry: "CN" },
            sameAs: ["https://mangains.github.io/"],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: siteInfo.name,
            url: "https://mangains.github.io/",
            description: siteInfo.description,
            inLanguage: "zh-CN",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://mangains.github.io/category?keyword={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "漫画网 - 漫画阅读器",
            url: "https://mangains.github.io/",
            description: "漫画网提供专业漫画阅读器，支持漫画翻页模式、漫画横屏阅读、漫画夜间模式，还有漫画缓存下载与漫画阅读设置。",
            inLanguage: "zh-CN",
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [{ "@type": "ListItem", position: 1, name: "首页", item: "https://mangains.github.io/" }],
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "如何开启漫画夜间模式？", acceptedAnswer: { "@type": "Answer", text: "点击页面右上角的月亮图标即可一键切换夜间模式，也可在漫画阅读器的阅读设置中开启，护眼更舒适。" } },
              { "@type": "Question", name: "漫画支持缓存下载吗？", acceptedAnswer: { "@type": "Answer", text: "支持。在漫画详情页或阅读器中点击缓存按钮，即可将章节下载到本地，离线也能随时阅读。" } },
              { "@type": "Question", name: "翻页模式和横屏阅读如何使用？", acceptedAnswer: { "@type": "Answer", text: "进入阅读器后，可在底部工具栏切换翻页模式与横屏阅读，选择最适合自己的阅读方式。" } },
            ],
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=colorful%20stylish%20manga%20comic%20collage%20illustration%20with%20dynamic%20action%20hero%20characters%2C%20comic%20panels%2C%20halftone%20dots%2C%20warm%20orange%20gold%20and%20crimson%20palette%2C%20energetic%20explosive%20composition%2C%20vibrant%20anime%20artwork&width=1600&height=900&seq=hero-manga-2026&orientation=landscape"
            alt="漫画网 - 海量漫画在线畅读"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/55 to-background-50"></div>
        </div>
        <div className="relative container-site pt-32 md:pt-44 pb-24 md:pb-32 w-full text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/15 backdrop-blur text-background-50 text-sm border border-background-50/25 mb-6">
            <i className="ri-flashlight-fill text-accent-500"></i>
            漫画翻页模式 · 横屏阅读 · 夜间模式
          </span>
          <h1 className="font-heading text-4xl md:text-6xl text-background-50 leading-tight">
            让看漫画更顺手
          </h1>
          <p className="mt-5 text-background-100 text-base md:text-lg max-w-2xl mx-auto">
            {siteInfo.name}提供专业漫画阅读器，支持漫画翻页模式、横屏阅读、夜间模式、缓存下载，海量热血、恋爱、悬疑、科幻漫画在线畅读。
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category"
              className="px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              立即开始阅读
            </Link>
            <Link
              to="/about"
              className="px-7 py-3 rounded-full bg-background-50/15 backdrop-blur text-background-50 font-semibold hover:bg-background-50/25 transition-colors whitespace-nowrap"
            >
              了解我们
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container-site py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {[
            { icon: "ri-arrow-left-right-line", title: "翻页模式", desc: "像翻书一样自然的漫画翻页体验" },
            { icon: "ri-smartphone-line", title: "横屏阅读", desc: "横屏大屏畅读，漫画细节尽收眼底" },
            { icon: "ri-moon-line", title: "夜间模式", desc: "暗色护眼阅读，深夜追漫不伤眼" },
            { icon: "ri-download-2-line", title: "缓存下载", desc: "一键缓存，离线也能随时看漫画" },
          ].map((f) => (
            <div key={f.title} className="rounded-xl border border-background-200 bg-background-100 p-5 flex flex-col items-start">
              <span className="w-11 h-11 rounded-lg bg-primary-500 flex items-center justify-center text-background-50">
                <i className={`${f.icon} text-lg`}></i>
              </span>
              <h3 className="mt-4 font-semibold text-foreground-950">{f.title}</h3>
              <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 热门推荐 */}
      <section className="container-site pb-14 md:pb-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">热门推荐</h2>
            <p className="mt-1 text-sm text-foreground-600">本站人气最高的精品漫画</p>
          </div>
          <Link to="/category" className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
            查看更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {hot.map((m) => (
            <MangaCard key={m.id} manga={m} />
          ))}
        </div>
      </section>

      {/* 分类导航 */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="container-site">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">浏览漫画分类</h2>
            <p className="mt-1 text-sm text-foreground-600">找到你钟爱的漫画题材</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {categoryCards.map((cat) => (
              <Link
                key={cat.name}
                to={`/category?type=${encodeURIComponent(cat.name)}`}
                className="group rounded-xl border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors"
              >
                <div className="relative h-32 md:h-40 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={`${cat.name}漫画分类`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 to-transparent flex items-end p-4">
                    <span className="text-background-50 font-semibold text-base flex items-center gap-1.5">
                      <i className="ri-folder-fill text-accent-500"></i>
                      {cat.name}漫画
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 最近更新 */}
      <section className="container-site py-14 md:py-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">最近更新</h2>
            <p className="mt-1 text-sm text-foreground-600">追更不迷路，新章节抢先看</p>
          </div>
          <Link to="/category" className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
            全部更新 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {latest.map((m) => (
            <MangaCard key={m.id} manga={m} />
          ))}
        </div>
      </section>

      {/* 排行榜 + 精选 */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="container-site grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
              <i className="ri-fire-fill text-primary-500"></i> 人气排行榜
            </h2>
            <p className="mt-1 text-sm text-foreground-600">根据全网人气热度实时排名</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ranking.map((m, idx) => (
                <Link
                  key={m.id}
                  to={`/manga/${m.id}`}
                  className="group flex items-center gap-3 rounded-lg p-2 hover:bg-background-100 transition-colors"
                >
                  <span
                    className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center font-heading text-lg ${
                      idx < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <div className="w-12 h-16 rounded overflow-hidden shrink-0">
                    <img src={m.cover} alt={m.title} className="w-full h-full object-cover object-top" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-foreground-950 truncate group-hover:text-primary-600 transition-colors">{m.title}</p>
                    <p className="text-xs text-foreground-600 mt-0.5 truncate">{m.latestChapter}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-foreground-600">{m.views}</p>
                    <p className="flex items-center justify-end gap-0.5 text-xs text-accent-700 mt-0.5">
                      <i className="ri-star-fill"></i>{m.rating}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">本周精选</h2>
            <p className="mt-1 text-sm text-foreground-600">编辑精挑细选的必读佳作</p>
            <div className="mt-6 space-y-4">
              {featured.map((m, idx) => (
                <div key={m.id} className="flex items-center gap-3">
                  <div className="relative w-20 h-28 rounded overflow-hidden shrink-0">
                    <img src={m.cover} alt={m.title} className="w-full h-full object-cover object-top" loading="lazy" />
                    <span className="absolute top-1 left-1 w-5 h-5 rounded bg-background-950/70 text-background-50 text-[11px] flex items-center justify-center font-semibold">{idx + 1}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link to={`/manga/${m.id}`} className="font-semibold text-sm text-foreground-950 hover:text-primary-600 transition-colors truncate block">
                      {m.title}
                    </Link>
                    <p className="text-xs text-foreground-600 mt-1 truncate">{m.description.slice(0, 28)}...</p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900 text-[11px]">{m.category}</span>
                      <StarRating rating={m.rating} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=night%20reading%20manga%20on%20a%20tablet%20in%20a%20cozy%20dim%20room%20with%20warm%20lamp%20light%2C%20relaxing%20reading%20atmosphere%2C%20stylish%20illustration%2C%20dark%20background%20with%20warm%20orange%20glow&width=1600&height=700&seq=cta-manga-2026&orientation=landscape"
            alt="夜间模式漫画阅读"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background-950/70"></div>
        </div>
        <div className="relative container-site w-full text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-background-50">开启你的漫画阅读之旅</h2>
          <p className="mt-3 text-background-100 max-w-xl mx-auto">支持漫画翻页模式、横屏阅读、夜间模式与缓存下载，随时随地畅享漫画乐趣。</p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/category" className="px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              马上阅读漫画
            </Link>
            <Link to="/about" className="px-7 py-3 rounded-full bg-background-50/15 backdrop-blur text-background-50 font-semibold hover:bg-background-50/25 transition-colors whitespace-nowrap">
              关于漫画网
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-site py-14 md:py-20">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">常见问题</h2>
          <p className="mt-1 text-sm text-foreground-600">关于漫画阅读器你可能想知道的事</p>
        </div>
        <div className="max-w-3xl mx-auto space-y-3">
          {[
            { q: "如何开启漫画夜间模式？", a: "点击页面右上角的月亮图标即可一键切换夜间模式，也可在漫画阅读器的阅读设置中开启，护眼更舒适。" },
            { q: "漫画支持缓存下载吗？", a: "支持。在漫画详情页或阅读器中点击缓存按钮，即可将章节下载到本地，离线也能随时阅读。" },
            { q: "翻页模式和横屏阅读如何使用？", a: "进入阅读器后，可在底部工具栏切换翻页模式与横屏阅读，选择最适合自己的阅读方式。" },
            { q: "漫画网站是免费的吗？", a: "漫画网提供大量免费漫画供用户在线畅读，并持续更新高质量作品，让看漫画更顺手。" },
          ].map((item) => (
            <details key={item.q} className="group rounded-xl border border-background-200 bg-background-100 p-5 open:bg-background-50 transition-colors">
              <summary className="flex items-center justify-between gap-3 cursor-pointer list-none font-medium text-foreground-950">
                {item.q}
                <i className="ri-add-line text-foreground-600 group-open:rotate-45 transition-transform shrink-0"></i>
              </summary>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}